import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useParams,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Styles and Theme
import "./index.css";
import rsTheme from "./theme/theme";

// Components
import NavBar from "./components/common/NavBar";
import { NotificationStack } from "./components/ui";
import NotAuthorized from "./components/common/NotAuthorized";
import PageNotFound from "./components/common/PageNotFound";
import RequireAuth from "./components/common/RequireAuth";
import VersionUpdateBanner from "./components/common/VersionUpdateBanner";

// Pages
import {
  DailyCost,
  DailyDispatch,
  Schedule,
  DevShoppingMall,
  TicketDemo,
  Drive,
  ErrorTest,
  Home,
  HoursLog,
  WorkerClock,
  UploadMedia,
  WorkerSchedule,
  WorkerTimesheet,
  PayoutTimesheet,
  PayoutTimesheetDashboard,
  TMLog,
  TMReport,
  TMPrice,
  TMInspector,
  RFILog,
  RFIDetail,
  SafetyMeeting,
  SMSTerms,
} from "./pages";
import Login from "./pages/Login";

// Hooks and Context
import {
  PlanningProvider,
  usePlanningContext,
  ScheduleProvider,
} from "./context/domain/planning";
import useCoreCtx, { CoreContext } from "./context/global/CoreContext";
import { VersionUpdateProvider, useVersionUpdate } from "./context/global/VersionUpdateContext";
import useTimekeepingCtx, {
  TimekeepingContext,
} from "./context/domain/timekeeping/TimekeepingContext";
import useFilesCtx, { FilesContext } from "./context/domain/files/FilesContext";
import useTMLogCtx, {
  TMLogContext,
} from "./context/domain/operations/TMLogContext";
import useRFICtx, {
  RFIContext,
} from "./context/domain/operations/RFIContext";
import { FEATURE_FLAGS } from "./config";

// Redirect components for handling dynamic parameters
const DispatchRedirect = () => {
  const { date } = useParams();
  return (
    <Navigate
      to={date ? `/planning/dispatch/${date}` : "/planning/dispatch"}
      replace
    />
  );
};

const ClockInRedirect = () => {
  const { date } = useParams();
  return (
    <Navigate
      to={date ? `/timekeeping/clock-in/${date}` : "/timekeeping/clock-in"}
      replace
    />
  );
};

const HoursLogRedirect = () => {
  const { date } = useParams();
  return (
    <Navigate
      to={date ? `/timekeeping/hours-log/${date}` : "/timekeeping/hours-log"}
      replace
    />
  );
};

// Route configuration for better organization
const TimekeepingRoutes = () => {
  const timekeepingCtx = useTimekeepingCtx();

  return (
    <TimekeepingContext.Provider value={timekeepingCtx}>
      <Routes>
        {/* Clock in and out */}
        <Route
          path="/clock-in/:date?"
          element={
            <RequireAuth>
              <WorkerClock />
            </RequireAuth>
          }
        />
        {/* Weekly timesheet and schedule */}
        <Route
          path="/worker-schedule/:date/user/:userId"
          element={
            <RequireAuth>
              <WorkerSchedule />
            </RequireAuth>
          }
        />
        <Route
          path="/worker-schedule/:date"
          element={
            <RequireAuth>
              <WorkerSchedule />
            </RequireAuth>
          }
        />
        <Route
          path="/worker-schedule"
          element={
            <RequireAuth>
              <WorkerSchedule />
            </RequireAuth>
          }
        />
        {/* Hours log entry */}
        <Route
          path="/hours-log/:date?"
          element={
            <RequireAuth>
              <HoursLog />
            </RequireAuth>
          }
        />
        {/* Worker timesheet editing/signing */}
        <Route
          path="/worker-timesheet/:date?"
          element={
            <RequireAuth>
              <WorkerTimesheet />
            </RequireAuth>
          }
        />
        {/* Worker timesheet editing/signing */}
        <Route
          path="/worker-timesheet/:date?"
          element={
            <RequireAuth>
              <WorkerTimesheet />
            </RequireAuth>
          }
        />
        {/* Payout timesheet list */}
        <Route
          path="/payout-timesheets"
          element={
            <RequireAuth>
              <PayoutTimesheetDashboard />
            </RequireAuth>
          }
        />
        {/* Payout timesheet editing/signing */}
        <Route
          path="/payout-timesheet/:userId/:weekEnding"
          element={
            <RequireAuth>
              <PayoutTimesheet />
            </RequireAuth>
          }
        />
        {/* Legacy timesheet review route - redirect to payout list */}
        <Route
          path="/timesheets"
          element={<Navigate to="/timekeeping/payout-timesheets" replace />}
        />
      </Routes>
    </TimekeepingContext.Provider>
  );
};

// Route configuration for better organization
const FilesRoutes = () => {
  const driveCtx = useFilesCtx();

  return (
    <FilesContext.Provider value={driveCtx}>
      <Routes>
        {/* Files & Files */}
        <Route
          path="/upload"
          element={
            <RequireAuth>
              <UploadMedia />
            </RequireAuth>
          }
        />
        <Route
          path="/drive"
          element={
            <RequireAuth>
              <Drive />
            </RequireAuth>
          }
        />
      </Routes>
    </FilesContext.Provider>
  );
};

// Route configuration for better organization
const PlanningNotificationHost = () => {
  const { notificationItems, dismissNotification } = usePlanningContext();
  return (
    <NotificationStack
      items={notificationItems ?? []}
      onDismiss={dismissNotification}
    />
  );
};

// Route configuration for better organization
const PlanningRoutes = () => {
  return (
    <PlanningProvider>
      <PlanningNotificationHost />
      <Routes>
        {/* Planning apps */}
        <Route
          path="/dispatch/:date?"
          element={
            <RequireAuth>
              <DailyDispatch />
            </RequireAuth>
          }
        />
        <Route
          path="/schedule"
          element={
            <RequireAuth>
              <ScheduleProvider>
                <Schedule />
              </ScheduleProvider>
            </RequireAuth>
          }
        />
      </Routes>
    </PlanningProvider>
  );
};

// Route configuration for better organization
const AnalyticsRoutes = () => {
  return (
    <Routes>
      {/* Analytics apps */}
      <Route
        path="/cost"
        element={
          <RequireAuth>
            <DailyCost />
          </RequireAuth>
        }
      />
    </Routes>
  );
};

// T&M pages wrapper - only T&M pages get TMLogContext
const TMLogWrapper = ({ children }) => {
  const tmLogCtx = useTMLogCtx();
  return <TMLogContext.Provider value={tmLogCtx}>{children}</TMLogContext.Provider>;
};

// RFI pages wrapper - only RFI pages get RFIContext
const RFIWrapper = ({ children }) => {
  const rfiCtx = useRFICtx();
  return <RFIContext.Provider value={rfiCtx}>{children}</RFIContext.Provider>;
};

// Route configuration for better organization
const OperationsRoutes = () => {
  return (
    <Routes>
      {/* T&M Log pages - wrapped with TMLogContext */}
      <Route
        path="/tm-log"
        element={
          <RequireAuth>
            <TMLogWrapper>
              <TMLog />
            </TMLogWrapper>
          </RequireAuth>
        }
      />
      <Route
        path="/tm-log/report/:id?"
        element={
          <RequireAuth>
            <TMLogWrapper>
              <TMReport />
            </TMLogWrapper>
          </RequireAuth>
        }
      />
      <Route
        path="/tm-log/price/:reportId"
        element={
          <RequireAuth>
            <TMLogWrapper>
              <TMPrice />
            </TMLogWrapper>
          </RequireAuth>
        }
      />
      {/* RFI Log pages - wrapped with RFIContext */}
      <Route
        path="/rfi-log"
        element={
          <RequireAuth>
            <RFIWrapper>
              <RFILog />
            </RFIWrapper>
          </RequireAuth>
        }
      />
      <Route
        path="/rfi/:id"
        element={
          <RequireAuth>
            <RFIWrapper>
              <RFIDetail />
            </RFIWrapper>
          </RequireAuth>
        }
      />
      {/* Safety Meetings page - no ticket context needed */}
      <Route
        path="/safetymeeting"
        element={
          <RequireAuth>
            <SafetyMeeting />
          </RequireAuth>
        }
      />
    </Routes>
  );
};

// Route configuration for better organization
const DevRoutes = () => {
  return (
    <Routes>
      {/* Dev and example pages */}
      <Route
        path="/shopping"
        element={
          <RequireAuth>
            <DevShoppingMall />
          </RequireAuth>
        }
      />
      <Route
        path="/error-test"
        element={
          <RequireAuth>
            <ErrorTest />
          </RequireAuth>
        }
      />
      <Route
        path="/ticket-demo"
        element={
          <RequireAuth>
            <TicketDemo />
          </RequireAuth>
        }
      />
    </Routes>
  );
};

const AppRoutes = () => (
  <Routes>
    {/* Home */}
    <Route
      path="/"
      element={
        <RequireAuth>
          <Home />
        </RequireAuth>
      }
    />
    <Route path="/home" element={<Navigate to="/" replace />} />

    {/* SMS Terms Page - Public route */}
    <Route path="/sms-terms" element={<SMSTerms />} />

    {/* Legacy URL Redirects - DO NOT USE - redirect old URLs to new prefixed URLs */}
    <Route
      path="/dispatch"
      element={<Navigate to="/planning/dispatch" replace />}
    />
    <Route path="/dispatch/:date" element={<DispatchRedirect />} />
    <Route path="/cost" element={<Navigate to="/analytics/cost" replace />} />
    <Route path="/drive" element={<Navigate to="/files/drive" replace />} />
    <Route path="/upload" element={<Navigate to="/files/upload" replace />} />
    <Route
      path="/clock-in"
      element={<Navigate to="/timekeeping/clock-in" replace />}
    />
    <Route path="/clock-in/:date" element={<ClockInRedirect />} />
    <Route
      path="/weekly-timesheet"
      element={<Navigate to="/timekeeping/worker-schedule" replace />}
    />
    <Route
      path="/hours-log"
      element={<Navigate to="/timekeeping/hours-log" replace />}
    />
    <Route path="/hours-log/:date" element={<HoursLogRedirect />} />
    <Route
      path="/worker-timesheet"
      element={<Navigate to="/timekeeping/worker-timesheet" replace />}
    />

    {/* Additional common redirects */}
    <Route
      path="/timekeeping"
      element={<Navigate to="/timekeeping/clock-in" replace />}
    />
    <Route
      path="/planning"
      element={<Navigate to="/planning/dispatch" replace />}
    />
    <Route path="/files" element={<Navigate to="/files/drive" replace />} />
    <Route
      path="/analytics"
      element={<Navigate to="/analytics/cost" replace />}
    />

    {/* New Prefixed Routes with Context */}
    <Route path="/timekeeping/*" element={<TimekeepingRoutes />} />
    <Route path="/files/*" element={<FilesRoutes />} />
    <Route path="/planning/*" element={<PlanningRoutes />} />
    <Route path="/analytics/*" element={<AnalyticsRoutes />} />
    <Route path="/operations/*" element={<OperationsRoutes />} />
    <Route path="/dev/*" element={<DevRoutes />} />

    {/* Catch-all for 404 etc */}
    <Route path="/not-authorized" element={<NotAuthorized />} />
    <Route path="*" element={<PageNotFound />} />
  </Routes>
);

// Authenticated app with CoreContext - only rendered for non-public routes
const AuthenticatedApp = () => {
  const coreCtx = useCoreCtx();
  const { notifyNewVersion } = useVersionUpdate();

  // Connect version check service callback to context
  useEffect(() => {
    if (FEATURE_FLAGS.ENABLE_VERSION_CHECK && window.__versionCheckService) {
      window.__versionCheckService.setOnNewVersionCallback(notifyNewVersion);
    }
  }, [notifyNewVersion]);

  return (
    <CoreContext.Provider value={coreCtx}>
      <NavBar />
      <VersionUpdateBanner />
      <AppRoutes />
    </CoreContext.Provider>
  );
};

// Components inside Router that need access to routing
const AppWithContext = () => {
  // Check if we're on a public route that doesn't need CoreContext
  const isPublicRoute =
    window.location.pathname.startsWith('/app/inspector/') ||
    window.location.pathname.startsWith('/app/login');

  return (
    <VersionUpdateProvider>
      <div className="bg-gray-100 font-inter caret-transparent min-h-screen w-full min-w-0">
        <ThemeProvider theme={rsTheme}>
          <CssBaseline />
          {isPublicRoute ? (
            // Public routes render without CoreContext to avoid auth API calls
            <Routes>
              <Route path="/inspector/:token" element={<TMInspector />} />
              <Route path="/login" element={<Login />} />
            </Routes>
          ) : (
            // Authenticated routes with CoreContext
            <AuthenticatedApp />
          )}
        </ThemeProvider>
      </div>
    </VersionUpdateProvider>
  );
};

export default function App() {
  return (
    <Router basename="/app">
      <AppWithContext />
    </Router>
  );
}
